import React, { FC } from 'react';
import { useWindowSize } from '../../../../hooks/useWindowSize';
import PortfolioLiquidation from './charts/portfolioLiquidation/portfolioLiquidation.component';
import { DatePicker } from '../../../../types/redux/pages/PagesStore';
import {
  getMostRecentWeekday,
  getWeekdayXMonthsAgo,
} from '../../../../utilities/dateUtilities';
import { useSelector } from 'react-redux';
import { createSpecificTitleBarDatePickerSelector } from '../../../../redux/pages/selectors';
import { useAdditionalTitleBarDatePicker } from '../../../../hooks/useAdditionalTitleBarComponents';
import dayjs from 'dayjs';
import MaximumOneDay from './charts/maximumOneDay/maximumOneDay.component';
import LiquidationCost from './charts/liquidationCost/liquidationCost.component';
import HistoricalNav from './charts/historicalNav.component.tsx/historicalNav.component';
import { FundInfoComponentProps } from '../../../layout/general/SimpleGeneralPageWrapper';
import { QueryBoundary } from '../../../utilityComponents/QueryBoundary';
import { useLiquidityRiskEvolutionPageModel } from '../../../../api/rs-hg/liquidity/hooks/queries';

const LiquidityRiskEvolutionPage: FC<FundInfoComponentProps> = (props) => {
  const startDateWeekday = getWeekdayXMonthsAgo(1);
  const endDateWeekday = getMostRecentWeekday();

  const startDatePicker: DatePicker = {
    titleBarKey: 'startDate',
    titleBarTitle: 'Select Start Date: ',
    currentValue: dayjs(startDateWeekday).format('YYYY-MM-DD'),
    displayOnOverviewPage: false,
  };

  const endDatePicker: DatePicker = {
    titleBarKey: 'endDate',
    titleBarTitle: 'Select End Date: ',
    currentValue: dayjs(endDateWeekday).format('YYYY-MM-DD'),
    displayOnOverviewPage: false,
  };

  useAdditionalTitleBarDatePicker(startDatePicker);
  useAdditionalTitleBarDatePicker(endDatePicker);

  const startDate = useSelector(
    createSpecificTitleBarDatePickerSelector('startDate'),
  );
  const endDate = useSelector(
    createSpecificTitleBarDatePickerSelector('endDate'),
  );
  const startDateProper = startDate[0] ? startDate[0].currentValue : null;
  const endDateProper = endDate[0] ? endDate[0].currentValue : null;

  const evolutionQuery = useLiquidityRiskEvolutionPageModel(
    props.fundId,
    startDateProper,
    endDateProper,
  );

  const { height, width } = useWindowSize();
  const renderHeight = (height - 296) / 2;
  const renderWidth = width * 0.4;

  return (
    <QueryBoundary
      isLoading={evolutionQuery.isLoading}
      isFetching={evolutionQuery.isFetching}
      isError={evolutionQuery.isError}
      error={evolutionQuery.error}
      data={evolutionQuery.data?.data}
      loadingMessages={['Loading liquidity risk evolution…']}
      noDataMessage="No liquidity risk evolution data available."
    >
      {(pageModel) => (
        <>
          <PortfolioLiquidation
            evolutionPayload={pageModel}
            isFetching={evolutionQuery.isFetching}
            error={evolutionQuery.error}
            height={renderHeight}
            dimensions={{ xs: 6 }}
          />
          <MaximumOneDay
            evolutionPayload={pageModel}
            isFetching={evolutionQuery.isFetching}
            error={evolutionQuery.error}
            height={renderHeight}
            dimensions={{ xs: 6 }}
          />
          <LiquidationCost
            evolutionPayload={pageModel}
            isFetching={evolutionQuery.isFetching}
            error={evolutionQuery.error}
            height={renderHeight}
            dimensions={{ xs: 6 }}
          />
          <HistoricalNav
            evolutionPayload={pageModel}
            isFetching={evolutionQuery.isFetching}
            error={evolutionQuery.error}
            height={renderHeight}
            width={renderWidth}
            dimensions={{ xs: 6 }}
          />
        </>
      )}
    </QueryBoundary>
  );
};

export default LiquidityRiskEvolutionPage;
